<template>
  <div
    :id="`${source}_talktime_tracker`"
    class="flex flex-col gap-4 py-3 overflow-y-scroll relative"
    :style="{ maxHeight: maxHeight }"
  >
    <div
      v-for="(participant, participantIndex) in selectedParticipantsData"
      :key="participantIndex"
      class="flex flex-col gap-2"
    >
      <div class="flex flex-row items-center justify-between">
        <div class="flex flex-row items-center gap-2 font-normal">
          <ProfileIcon :data="participant" />
          <span class="text-[11px] primary_color">{{ participant.name }}</span>
        </div>

        <span class="text-[11px] secondary_color"
          >{{ participant.talkPercentage.toFixed(1) }}% Talktime</span
        >
      </div>

      <div
        class="relative timeline-row"
        :ref="(el) => setTimelineRef(el, participantIndex)"
      >
        <div class="timeline_container rounded-lg relative overflow-hidden">
          <div
            v-for="(segment, index) in participant.segments"
            :key="index"
            class="absolute h-full rounded-[2px] cursor-pointer transition-opacity hover:opacity-80"
            :style="{
              left: `${(segment.start_time / totalDuration) * 100}%`,
              width: `${((segment.end_time - segment.start_time) / totalDuration) * 100}%`,
              background: 'var(--recording-timeline)',
            }"
            @click="seekToTime(segment.start_time)"
            :title="`${formatSecondsToTime(segment.start_time)} - ${formatSecondsToTime(segment.end_time)}: ${segment.text}`"
          ></div>
        </div>
      </div>
    </div>

    <div
      v-if="showPlaybackIndicator && timelinePositions.length > 0"
      class="timeline_indicator absolute w-[1px] z-20 pointer-events-none"
      :style="{
        left: `${playbackIndicatorLeft}%`,
        top: `${firstTimelineTop}px`,
        height: `${timelineSpanHeight}px`,
      }"
    ></div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { formatSecondsToTime } from "@/src/utils/dateTimeUtils.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";

const props = defineProps({
  source: { type: String, required: true },
  maxHeight: { type: String, default: "" },
});

const store = useStore();

const timelineRefs = ref([]);
const timelinePositions = ref([]);

const setTimelineRef = (el, index) => {
  if (el) {
    timelineRefs.value[index] = el;
  }
};

const updateTimelinePositions = async () => {
  await nextTick();
  timelinePositions.value = timelineRefs.value
    .map((ref) => {
      if (ref) {
        const rect = ref.getBoundingClientRect();
        const containerRect = ref
          .closest(`#${props.source}_talktime_tracker`)
          .getBoundingClientRect();
        return {
          top: rect.top - containerRect.top,
          height: rect.height,
        };
      }
      return null;
    })
    .filter(Boolean);
};

const transcriptData = computed(
  () => store.getters["meetings/recordingTranscriptData"] || [],
);
const participantsData = computed(
  () => store.getters["meetings/recordingParticipantsData"] || {},
);
const selectedParticipantsIds = computed(
  () => store.getters["meetings/recordingSelectedParticipants"] || [],
);
const users = computed(() => store.getters["users/users"] || []);
const currentTime = computed(
  () => store.getters["meetings/recordingCurrentTime"] || 0,
);

const totalDuration = computed(() => {
  if (transcriptData.value.length === 0) return 0;
  return Math.max(...transcriptData.value.map((item) => item.end_time));
});

const selectedParticipantsData = computed(() => {
  try {
    const allParticipantsIds = Object.keys(participantsData.value);

    return selectedParticipantsIds.value
      .filter((participantId) => allParticipantsIds.includes(participantId))
      .sort((a, b) => {
        const indexA = allParticipantsIds.indexOf(a);
        const indexB = allParticipantsIds.indexOf(b);
        return indexA - indexB;
      })
      .map((participantId) => {
        const name =
          participantsData.value[participantId] ||
          `Participant ${participantId}`;
        const segments = transcriptData.value.filter(
          (item) =>
            item.speaker === name || item.participant_id === participantId,
        );

        const totalTalkTime = segments.reduce(
          (acc, segment) => acc + (segment.end_time - segment.start_time),
          0,
        );

        const talkPercentage =
          totalDuration.value > 0
            ? (totalTalkTime / totalDuration.value) * 100
            : 0;

        const lowerName = name.toLowerCase();
        const matchedUser = users.value.find(
          (u) =>
            u.name?.toLowerCase() === lowerName ||
            u.email?.toLowerCase() === lowerName,
        );

        return {
          id: matchedUser?.id,
          name,
          avatar_color: matchedUser?.avatar_color || null,
          segments,
          totalTalkTime,
          talkPercentage,
        };
      });
  } catch (error) {
    console.error(
      "An error occurred while computing selected participants data: ",
      error,
    );
    return [];
  }
});

const playbackPosition = computed(() => {
  if (totalDuration.value === 0) return 0;
  return (currentTime.value / totalDuration.value) * 100;
});

const playbackIndicatorLeft = computed(() => {
  return playbackPosition.value;
});

const firstTimelineTop = computed(() => {
  return timelinePositions.value.length > 0
    ? timelinePositions.value[0].top
    : 0;
});

const timelineSpanHeight = computed(() => {
  if (timelinePositions.value.length === 0) return 0;
  const firstTop = timelinePositions.value[0].top;
  const lastPosition =
    timelinePositions.value[timelinePositions.value.length - 1];
  const lastBottom = lastPosition.top + lastPosition.height;
  return lastBottom - firstTop;
});

const showPlaybackIndicator = computed(
  () => selectedParticipantsIds.value.length > 0 && totalDuration.value > 0,
);

const seekToTime = (time) => {
  store.dispatch("meetings/seekRecordingToTime", time);
};

onMounted(() => {
  updateTimelinePositions();
});

watch(selectedParticipantsData, () => {
  updateTimelinePositions();
});
</script>

<style scoped>
.timeline_container {
  height: 4px;
  align-self: stretch;
  background: var(--white-opacity-8);
}

.timeline_indicator {
  background: var(--foreground-rgb);
}

.timeline-row {
  position: relative;
}
</style>
